import { css, cva } from '@styled-system/css';
import {
  CalendarDays,
  CalendarRange,
  Check,
  Info,
  Plus,
  RefreshCcw,
  ShoppingBag,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';

import { useQueryClient } from '@tanstack/react-query';

import {
  useGetUserNotificationSettingsQuery,
  useGetVeilleNotificationConfigQuery,
  useGetVeilleNotificationHistoryQuery,
  useUpdateUserNotificationSettingsMutation,
  useUpdateVeilleNotificationConfigMutation,
} from '@/shared/hooks/queries/generated';
import { Spinner } from '@/shared/components/ui/Spinner';

import type { VeilleView } from '../veilleTypes';

type VeilleKpi = 'sales' | 'newItems' | 'sellers' | 'averagePrice' | 'sellThroughRate';
type VeilleRuleWindow = 'day' | 'week' | 'hour';

const VEILLE_KPIS: VeilleKpi[] = ['sales', 'newItems', 'sellers', 'averagePrice', 'sellThroughRate'];
const HOURLY_KPIS: VeilleKpi[] = ['sales', 'newItems'];

interface VeilleNotificationRule {
  id: string;
  kpi: VeilleKpi;
  direction: 'above' | 'below';
  threshold: number;
  window: VeilleRuleWindow;
}

/** thresholds serveur { kpi: { fenêtre: { above?, below? } } } -> lignes UI. */
const thresholdsToRules = (thresholds: Record<string, unknown> | null | undefined): VeilleNotificationRule[] => {
  const rules: VeilleNotificationRule[] = [];
  if (!thresholds) return rules;
  for (const kpi of VEILLE_KPIS) {
    const windows = thresholds[kpi] as Record<string, { above?: number; below?: number }> | undefined;
    if (!windows) continue;
    for (const window of ['day', 'week', 'hour'] as const) {
      const rule = windows[window];
      if (!rule) continue;
      if (rule.above != null) rules.push({ id: `${kpi}-${window}-above`, kpi, window, direction: 'above', threshold: rule.above });
      if (rule.below != null) rules.push({ id: `${kpi}-${window}-below`, kpi, window, direction: 'below', threshold: rule.below });
    }
  }
  return rules;
};

/** Lignes UI -> objet thresholds serveur (remplacé INTÉGRALEMENT à l'écriture). */
const rulesToThresholds = (rules: VeilleNotificationRule[]): Record<string, unknown> | null => {
  if (rules.length === 0) return null;
  const thresholds: Record<string, Record<string, Record<string, number>>> = {};
  for (const rule of rules) {
    thresholds[rule.kpi] ??= {};
    thresholds[rule.kpi][rule.window] ??= {};
    thresholds[rule.kpi][rule.window][rule.direction] = rule.threshold;
  }
  return thresholds;
};

// ============================================================================
// Onglet Notifications de la veille (MOCK, contrat serveur 2026-08-11/12) :
// - le webhook Discord n'est JAMAIS relu : état « configuré » + remplacer/
//   retirer, aucun champ pré-rempli ;
// - email : réglage stocké mais AUCUN envoi (« bientôt actif ») ;
// - seuils : fenêtres day/week/hour (hour réservé aux volumes, hour.above>=1,
//   hour.below>=0), directions above/below, textes d'aide imposés (hystérésis
//   horaire, ré-émission nocturne day/week, sous-comptage similarité) ;
// - historique : journal des remises {kind, ok, reason, label}, rétention 14 j,
//   échec = perte DÉFINITIVE (ligne barrée, jamais « en attente »).
// ============================================================================

const wrap = css({ display: 'flex', flexDirection: 'column', gap: '8', paddingTop: '3', maxWidth: '820px' });

const section = css({ display: 'flex', flexDirection: 'column', gap: '4' });

const sectionTitle = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

const card = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '5',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
});

const toggleRow = css({ display: 'flex', alignItems: 'center', gap: '3' });

const toggleHint = css({ fontSize: 'caption', color: 'text.tertiary', marginLeft: '7', display: 'block' });

const ruleRow = css({ display: 'flex', alignItems: 'center', gap: '3', flexWrap: 'wrap' });

const helpNote = css({
  display: 'flex',
  gap: '3',
  alignItems: 'flex-start',
  fontSize: 'caption',
  color: 'text.tertiary',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  borderRadius: 'md',
  paddingY: '3',
  paddingX: '4',
  lineHeight: 1.55,
});

const configuredChip = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1.5',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'full',
  fontSize: 'caption',
  fontWeight: 'medium',
  bg: 'success.soft',
  color: 'success.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'success.border',
  whiteSpace: 'nowrap',
});

const failedBanner = css({
  display: 'flex',
  gap: '3',
  alignItems: 'flex-start',
  fontSize: 'caption',
  color: 'error.default',
  bg: 'error.soft',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'error.border',
  borderRadius: 'md',
  paddingY: '3',
  paddingX: '4',
  lineHeight: 1.55,
});

const historyRow = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    paddingY: '3',
    paddingX: '4',
    bg: 'surface.default',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.subtle',
    borderRadius: 'md',
    fontSize: 'caption',
    color: 'text.secondary',
  },
  variants: {
    failed: {
      true: { textDecoration: 'line-through', color: 'text.tertiary' },
      false: {},
    },
  },
});

const historyTime = css({ marginLeft: 'auto', color: 'text.tertiary', whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'none' });

export const VeilleNotifications: FC<{ veille: VeilleView }> = ({ veille }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
  const queryClient = useQueryClient();

  const configKey = ['GetVeilleNotificationConfig', { veilleId: veille.id }];
  const { data: configData, isLoading: configLoading } = useGetVeilleNotificationConfigQuery({ veilleId: veille.id });
  const config = configData?.getVeilleNotificationConfig ?? null;

  // Réglages DU COMPTE : le webhook vaut pour TOUTES les veilles (2026-08-15),
  // et il est relisible en clair par son propriétaire
  const { data: userSettingsData } = useGetUserNotificationSettingsQuery();
  const accountWebhook = userSettingsData?.getUserNotificationSettings.discordWebhook ?? null;
  const updateUserSettings = useUpdateUserNotificationSettingsMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['GetUserNotificationSettings'] });
      void queryClient.invalidateQueries({ queryKey: configKey });
    },
  });
  const updateMutation = useUpdateVeilleNotificationConfigMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: configKey });
      void queryClient.invalidateQueries({ queryKey: ['GetVeilleNotificationHistory'] });
    },
  });

  const [webhookDraft, setWebhookDraft] = useState('');
  const [webhookEditing, setWebhookEditing] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);

  // Projection UI de l'objet thresholds serveur
  const rules = useMemo(() => thresholdsToRules(config?.thresholds as Record<string, unknown> | null), [config]);

  const settings = {
    rules,
    eachSale: config?.eachSale ?? false,
    dailyReport: config?.dailyReport ?? false,
    weeklyReport: config?.weeklyReport ?? false,
    emailEnabled: config?.emailEnabled ?? false,
    notificationEmail: config?.notificationEmail ?? null,
    discordEnabled: config?.discordEnabled ?? false,
    lastDeliveryFailed: config?.lastDeliveryFailed ?? false,
    lastReason: config?.lastReason ?? null,
  };

  // PATCH : n'envoyer QUE le champ modifié (un champ absent est inchangé) —
  // ne JAMAIS inclure discordWebhook en sauvant le reste (il serait effacé)
  const update = (partial: Record<string, unknown>) =>
    updateMutation.mutate({ input: { veilleId: veille.id, ...partial } });

  // thresholds se remplace intégralement : on renvoie l'objet ENTIER reconstruit
  const saveRules = (nextRules: VeilleNotificationRule[]) => update({ thresholds: rulesToThresholds(nextRules) });

  const updateRule = (ruleId: string, partial: Partial<VeilleNotificationRule>) => {
    saveRules(settings.rules.map((rule) => (rule.id === ruleId ? { ...rule, ...partial } : rule)));
  };

  const addRule = () => {
    // Une seule règle par (kpi, fenêtre, direction) côté serveur : on choisit
    // une combinaison encore libre
    const taken = new Set(settings.rules.map((rule) => rule.id));
    for (const kpi of VEILLE_KPIS) {
      for (const window of ['day', 'week'] as VeilleRuleWindow[]) {
        for (const direction of ['above', 'below'] as const) {
          if (!taken.has(`${kpi}-${window}-${direction}`)) {
            saveRules([...settings.rules, { id: `${kpi}-${window}-${direction}`, kpi, window, direction, threshold: 10 }]);
            return;
          }
        }
      }
    }
  };

  // hour est réservé aux volumes : bascule -> le KPI est ramené aux éligibles
  const changeWindow = (rule: VeilleNotificationRule, window: VeilleRuleWindow) => {
    const kpi = window === 'hour' && !HOURLY_KPIS.includes(rule.kpi) ? 'sales' : rule.kpi;
    // hour.above à 0 est rejeté par le serveur : min 1 (hour.below à 0 reste légitime)
    const threshold =
      window === 'hour' && rule.direction === 'above' && rule.threshold < 1 ? 1 : rule.threshold;
    updateRule(rule.id, { window, kpi, threshold });
  };

  const changeDirection = (rule: VeilleNotificationRule, direction: 'above' | 'below') => {
    const threshold = rule.window === 'hour' && direction === 'above' && rule.threshold < 1 ? 1 : rule.threshold;
    updateRule(rule.id, { direction, threshold });
  };

  const saveWebhook = async () => {
    const url = webhookDraft.trim();
    if (!/^https:\/\/(?:canary\.|ptb\.)?discord(?:app)?\.com\/api\/webhooks\/.+/.test(url)) {
      setWebhookError(t('veille.webhookInvalid'));
      return;
    }
    try {
      await updateUserSettings.mutateAsync({ input: { discordWebhook: url } });
      setWebhookDraft('');
      setWebhookEditing(false);
      setWebhookError(null);
    } catch (error) {
      setWebhookError(error instanceof Error ? error.message : t('common.error'));
    }
  };

  const kpiOptionsFor = (rule: VeilleNotificationRule) =>
    (rule.window === 'hour' ? HOURLY_KPIS : VEILLE_KPIS).map((kpi) => ({ value: kpi, label: t(`veille.kpi.${kpi}`) }));
  const directionOptions = [
    { value: 'above', label: t('veille.ruleAbove') },
    { value: 'below', label: t('veille.ruleBelow') },
  ];
  const windowOptions = [
    { value: 'day', label: t('veille.ruleWindowDay') },
    { value: 'week', label: t('veille.ruleWindowWeek') },
    { value: 'hour', label: t('veille.ruleWindowHour') },
  ];

  const hasHourRule = settings.rules.some((rule) => rule.window === 'hour');
  const hasNightlyRule = settings.rules.some((rule) => rule.window !== 'hour');

  // Journal des remises serveur (rétention 14 j, tri décroissant)
  const { data: historyData } = useGetVeilleNotificationHistoryQuery(
    { veilleId: veille.id, limit: 20 },
    { staleTime: 30_000 },
  );
  const history = useMemo(
    () => (historyData?.getVeilleNotificationHistory ?? []).map((event, index) => ({ ...event, id: `${event.at}-${index}` })),
    [historyData],
  );

  const kindLabel = (kind: string) =>
    kind === 'single' ? t('veille.kindSingle') : kind === 'digest' ? t('veille.kindDigest') : t('veille.kindReport');

  const kindIcon = (kind: string, ok: boolean) =>
    !ok ? (
      <TriangleAlert size={13} strokeWidth={1.75} />
    ) : kind === 'single' ? (
      <ShoppingBag size={13} strokeWidth={1.75} />
    ) : kind === 'digest' ? (
      <CalendarDays size={13} strokeWidth={1.75} />
    ) : (
      <CalendarRange size={13} strokeWidth={1.75} />
    );

  if (configLoading) {
    return (
      <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '16' })}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className={wrap}>
      {/* ─── Canaux d'envoi ────────────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('veille.channelsTitle')}</span>
        <div className={card}>
          {/* Webhook Discord DU COMPTE : un seul salon pour toutes les
              veilles, relisible en clair, changement appliqué partout <= 30 s */}
          <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
            <span className={css({ fontSize: 'bodySm', fontWeight: 'semibold', color: 'text.primary' })}>
              {t('veille.channelDiscord')}
            </span>
            {accountWebhook && !webhookEditing ? (
              <div className={css({ display: 'flex', alignItems: 'center', gap: '3', flexWrap: 'wrap' })}>
                <span className={configuredChip}>
                  <Check size={11} strokeWidth={2} />
                  {t('veille.webhookConfigured')}
                </span>
                <span
                  className={css({
                    fontFamily: 'mono',
                    fontSize: 'micro',
                    color: 'text.tertiary',
                    maxWidth: '340px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  })}
                >
                  {accountWebhook}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<RefreshCcw size={12} strokeWidth={1.75} />}
                  onClick={() => {
                    setWebhookDraft(accountWebhook);
                    setWebhookEditing(true);
                  }}
                >
                  {t('veille.webhookReplace')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 size={12} strokeWidth={1.75} />}
                  handleClick={async () => {
                    await updateUserSettings.mutateAsync({ input: { discordWebhook: null } });
                  }}
                  showLoader
                >
                  {t('veille.webhookRemove')}
                </Button>
              </div>
            ) : (
              <div className={css({ display: 'flex', flexDirection: 'column', gap: '2', maxWidth: '520px' })}>
                <div className={css({ display: 'flex', gap: '3' })}>
                  <Input
                    value={webhookDraft}
                    placeholder="https://discord.com/api/webhooks/…"
                    state={webhookError ? 'error' : 'default'}
                    onChange={(event) => {
                      setWebhookDraft(event.target.value);
                      if (webhookError) setWebhookError(null);
                    }}
                  />
                  <Button variant="secondary" size="md" handleClick={saveWebhook} showLoader disabled={!webhookDraft.trim()}>
                    {t('common.save')}
                  </Button>
                  {webhookEditing && (
                    <Button variant="ghost" size="md" onClick={() => setWebhookEditing(false)}>
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>
                {webhookError && <span className={css({ fontSize: 'caption', color: 'error.default' })}>{webhookError}</span>}
                <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.webhookHint')}</span>
              </div>
            )}
            {/* Un seul salon pour toutes les veilles — appliqué partout au tour suivant */}
            <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.webhookGlobalHint')}</span>

            {/* Interrupteur PAR VEILLE — l'activation est refusée sans webhook */}
            <div>
              <div className={toggleRow}>
                <Checkbox
                  checked={settings.discordEnabled}
                  disabled={!settings.discordEnabled && accountWebhook == null}
                  onChange={(checked) => update({ discordEnabled: checked })}
                  label={t('veille.discordEnabledLabel')}
                />
              </div>
              {accountWebhook == null && <span className={toggleHint}>{t('veille.discordEnabledMissing')}</span>}
            </div>
          </div>

          {/* Email : canal ACTIF (2026-08-13). L'activation est refusée sans
              adresse primaire vérifiée -> case désactivée en amont */}
          <div>
            <div className={toggleRow}>
              <Checkbox
                checked={settings.emailEnabled}
                disabled={!settings.emailEnabled && settings.notificationEmail == null}
                onChange={(checked) => update({ emailEnabled: checked })}
                label={t('veille.channelEmail')}
              />
            </div>
            <span className={toggleHint}>
              {settings.notificationEmail
                ? t('veille.emailSentTo', { email: settings.notificationEmail })
                : t('veille.emailMissingHint')}
            </span>
          </div>

          {/* Aucun canal opérationnel : rien ne sera remis */}
          {!settings.discordEnabled && !settings.emailEnabled && (
            <div className={helpNote}>
              <Info size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>{t('veille.noChannelWarning')}</span>
            </div>
          )}

          {/* no_channel_configured : des notifications attendaient mais AUCUN
              canal n'était configuré — un état à corriger, pas un échec */}
          {settings.lastDeliveryFailed && settings.lastReason === 'no_channel_configured' && (
            <div className={helpNote}>
              <Info size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>{t('veille.reason.no_channel_configured')}</span>
            </div>
          )}

          {/* Diagnostic de la dernière remise : définitif, jamais de réessai */}
          {settings.lastDeliveryFailed && settings.lastReason !== 'no_channel_configured' && (
            <div className={failedBanner}>
              <TriangleAlert size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>
                {t('veille.lastDeliveryFailed')}{' '}
                {settings.lastReason ? t(`veille.reason.${settings.lastReason}`, t('veille.reason.unknown')) : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Rapports et alertes ───────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('veille.reportsTitle')}</span>
        <div className={card}>
          <div>
            <div className={toggleRow}>
              <Checkbox
                checked={settings.eachSale}
                onChange={(checked) => update({ eachSale: checked })}
                label={t('veille.eachSale')}
              />
            </div>
            {/* Regroupement automatique Discord : non désactivable, à expliquer */}
            <span className={toggleHint}>{t('veille.eachSaleHint')}</span>
          </div>
          <div>
            <div className={toggleRow}>
              <Checkbox
                checked={settings.dailyReport}
                onChange={(checked) => update({ dailyReport: checked })}
                label={t('veille.dailyReport')}
              />
            </div>
            <span className={toggleHint}>{t('veille.dailyReportHint')}</span>
          </div>
          <div>
            <div className={toggleRow}>
              <Checkbox
                checked={settings.weeklyReport}
                onChange={(checked) => update({ weeklyReport: checked })}
                label={t('veille.weeklyReport')}
              />
            </div>
            <span className={toggleHint}>{t('veille.weeklyReportHint')}</span>
          </div>
        </div>
      </div>

      {/* ─── Seuils de KPI ─────────────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('veille.rulesTitle')}</span>
        <div className={card}>
          {settings.rules.length === 0 && (
            <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.rulesEmpty')}</span>
          )}
          {settings.rules.map((rule) => (
            <div key={rule.id} className={ruleRow}>
              <Select
                value={rule.kpi}
                onChange={(value) => updateRule(rule.id, { kpi: value as VeilleKpi })}
                options={kpiOptionsFor(rule)}
                width="180px"
                size="sm"
              />
              <Select
                value={rule.direction}
                onChange={(value) => changeDirection(rule, value as 'above' | 'below')}
                options={directionOptions}
                width="170px"
                size="sm"
              />
              <div className={css({ width: '110px' })}>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={rule.window === 'hour' && rule.direction === 'above' ? 1 : 0}
                  value={String(rule.threshold)}
                  onChange={(event) => {
                    const raw = Number(event.target.value) || 0;
                    const min = rule.window === 'hour' && rule.direction === 'above' ? 1 : 0;
                    updateRule(rule.id, { threshold: Math.max(min, raw) });
                  }}
                />
              </div>
              <Select
                value={rule.window}
                onChange={(value) => changeWindow(rule, value as VeilleRuleWindow)}
                options={windowOptions}
                width="190px"
                size="sm"
              />
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 size={13} strokeWidth={1.75} />}
                onClick={() => saveRules(settings.rules.filter((r) => r.id !== rule.id))}
                aria-label={t('veille.ruleDelete')}
              />
            </div>
          ))}
          <div>
            <Button variant="secondary" size="sm" icon={<Plus size={13} strokeWidth={1.75} />} onClick={addRule}>
              {t('veille.ruleAdd')}
            </Button>
          </div>

          {/* Textes d'aide imposés par le contrat : comportements différents
              des fenêtres, à dire AU MOMENT de la saisie */}
          {hasHourRule && (
            <div className={helpNote}>
              <Info size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>{t('veille.hourHysteresisHint')}</span>
            </div>
          )}
          {hasHourRule && veille.similarity && (
            <div className={helpNote}>
              <TriangleAlert size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>{t('veille.hourSimilarityHint')}</span>
            </div>
          )}
          {hasNightlyRule && (
            <div className={helpNote}>
              <Info size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
              <span>{t('veille.nightlyRepeatHint')}</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Dernières notifications (journal des remises, 14 j) ───────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('veille.historyTitle')}</span>
        {history.length === 0 ? (
          <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.historyEmpty')}</span>
        ) : (
          <>
            {history.map((event) => (
              <div key={event.id} className={historyRow({ failed: !event.ok })}>
                {kindIcon(event.kind, event.ok)}
                {/* Ligne sans label (antérieure au journal) : repli sur le kind */}
                <span>{event.label ?? kindLabel(event.kind)}</span>
                {!event.ok && event.reason && (
                  <span className={css({ color: 'error.default', textDecoration: 'none', fontWeight: 'medium' })}>
                    {t(`veille.reason.${event.reason}`, t('veille.reason.unknown'))}
                  </span>
                )}
                <span className={historyTime}>
                  {new Date(event.at).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
            <span className={css({ fontSize: 'micro', color: 'text.tertiary' })}>
              {t('veille.historyRetention')}
              {settings.discordEnabled && settings.emailEnabled ? ` ${t('veille.historyDualChannel')}` : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
